import {BitmapWinCountUp} from "@nolimitcity/slot-game/bin/game/winpresentation/countup/BitmapWinCountUp";
import {Elastic, Power2, Power3, Timeline, TimelineLite, TweenLite} from "gsap";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {WGGameConfig} from "../../../../WGGameConfig";
import {SoundManager} from "../../../../resource/sound/SoundManager";
import {WGSoundConfig} from "../../../../WGSoundConfig";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/core/component/BitmapCountUp";


export class InitialWinCountup extends BitmapWinCountUp {

    private _isCountUpSoundPlaying: boolean = false;
    private _hasCountUpEndSoundPlayed: boolean = false;
    private _hasMultiplierEndSoundPlayed: boolean = false;
    private _isShowing: boolean = false;
    private _endValue: number;
    private _endMultiplier: number;

    public _isCountupAbort: boolean = false;
    private multiplierIndexesForSound: number = 0;
    private _multiplierCount: BitmapCountUp;

    constructor() {
        super({
            font: {
                name: "WGNumbersWinCountUp",
                size: 27
            },
            tint: parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
    }

    protected initAnimations(): void {
        super.initAnimations();
        this._multiplierCount = new BitmapCountUp({
            style: {
                font: {
                    name: "WGNumbersWinCountUp",
                    size: 15
                },
                tint: parseInt(("#ffffff").replace(/^#/, ''), 16)
            }
        });
        this._multiplierCount.visible = false;
        this.pivot.set(0.5);
    }


    public getCountUpAnimation(from: number, to: number, duration: number, multiplier: number): Timeline {
        this._countUpContainer.addChild(this._multiplierCount);
        this._countUpContainer.position.set(120 * 3.2, WGGameConfig.instance.GAME_HEIGHT / 1.8);
        this._multiplierCount.position.set(-40, this._countUp.position.y + this._countUp.height / 3);
        this._endValue = to;
        this._endMultiplier = multiplier;
        const oriNumber: number = to / multiplier;
        const timeline: TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this.setMaxTextWidth(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._multiplierCount.alpha = 0;
                this._multiplierCount.visible = false;
                this._countUpContainer.scale.set(1, 1);
                if (duration > 0) {
                    this._isCountUpSoundPlaying = true;
                    SoundManager.playSoundEffect(WGSoundConfig.instance.COUNTUP_SOUND, -1);
                } else {
                    // #373
                    if (!WGGameConfig.instance.WIN_BELOW_STAKE) {
                        SoundManager.playSoundEffect(WGSoundConfig.instance.COUNTUP_END_TINY);
                    }
                }
                this._isShowing = true;
            },
            this._countUp.getCountUpAnimation(from, oriNumber, {duration: duration, ease: Power3.easeInOut}),
        ]);

        timeline.add(() => {
            this.stopCountUpSound();
        });

        if (multiplier > 1) {
            const levelTime: number = 0.5 * multiplier > 5 ? MathHelper.roundToDecimals(5 / multiplier, 2) : 0.5;
            this._levelTime = levelTime;
            for (let m: number = 1; m <= multiplier; m++) {
                const showNumber: number = oriNumber * m;
                const scale: number = this.getScale(m, multiplier);
                let mIndex: number = (m % 2 === 0) ? (m % 2) + 2 : m / m;
                if (m > 1) {
                    if (m > 5 && m < 15) {
                        this._levelTime -= 0.01;
                    }

                    if (m === multiplier) {
                        timeline.add(() => {
                            this._hasMultiplierEndSoundPlayed = true;
                            // #373
                            if (!WGGameConfig.instance.WIN_BELOW_STAKE) {
                                SoundManager.playSoundEffect(WGSoundConfig.instance.MULTIPLIER_COUNTUP_END);
                            }
                        }, "-=0.119");
                    } else {
                        timeline.add(() => {
                            this.playMultiplierCountupSound();
                        });
                    }

                    timeline.add(() => {
                        this._multiplierCount.alpha = 1;
                        this._multiplierCount.visible = true;
                        this._multiplierCount.text = `x ${m}`
                        this._multiplierCount.position.x = -this._multiplierCount.width / 2; // to fix 334
                    });
                }

                timeline.add([
                    () => {
                    },
                    this._countUp.getCountUpAnimation(showNumber, showNumber, {duration: 0.01}),
                    TweenLite.to(this._countUpContainer.scale, this._levelTime / 2,
                        {
                            x: scale,
                            y: scale,
                            ease: Elastic.easeOut.config(2, 1)
                        }),
                ]);
                timeline.add(TweenLite.to(this, this._levelTime / 2, {}));
            }
        }
        return timeline;
    }

    private _levelTime: number = 0;

    private playMultiplierCountupSound(): void {
        // #373
        if (this._isCountupAbort || WGGameConfig.instance.WIN_BELOW_STAKE) return;

        SoundManager.playSoundEffect(WGSoundConfig.instance.MULTIPLIER_COUNTUP[this.nonRepeativeRandom()]);

    }


    private nonRepeativeRandom(): number {
        let randomNumber: number = this.multiplierIndexesForSound;
        while (randomNumber === this.multiplierIndexesForSound) {
            randomNumber = MathHelper.randomIntInRange(0, 3);
        }
        this.multiplierIndexesForSound = randomNumber;
        return randomNumber;
    }

    public abort(endValue: number, isAborted: boolean, multiplier: number, isBigWin: boolean): Timeline | null {
        if (isBigWin) {
            return null;
        }
        this._endValue = endValue;
        this._endMultiplier = multiplier;
        return super.abort(endValue, isAborted, multiplier);
    }

    public getEndingAnimation(endValue: number): Timeline {
        const timeline: TimelineLite = new TimelineLite({
            onComplete: () => {
                this._isCountUpSoundPlaying = false;
                this._hasCountUpEndSoundPlayed = false;
                this._hasMultiplierEndSoundPlayed = false;
            }
        });
        timeline.add(() => {
            this._isShowing = false;
            if (this._endMultiplier > 1 && !this._hasMultiplierEndSoundPlayed) {
                // EventHandler.dispatchEvent(new GameEvent(SoundEvent.PLAY_SOUND_EFFECT, BarbarianFurySoundConfig.instance.COUNT_UP_ENDS));
                this._hasCountUpEndSoundPlayed = true;
                this.stopCountUpSound();
            }
        });
        timeline.add(this._countUp.getCountUpAnimation(endValue, endValue, {duration: 0.01}));
        timeline.add([
            TweenLite.to(this._countUp, 0.5, {alpha: 0, ease: Power2.easeIn}),
            TweenLite.to(this._multiplierCount, 0.5, {alpha: 0, ease: Power2.easeIn}),
            TweenLite.to(this._countUpContainer, 0.5, {y: -100, ease: Power2.easeIn}),
        ], `+=0.2`); // stay a while after count up.
        timeline.add(() => this._countUpContainer.y = 0);
        return timeline;
    }

    public getAbortAnimation(endValue: number, multiplier: number): Timeline {
        const timeline: TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this.setMaxTextWidth(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.pivot.set(0.5);
                this._countUpContainer.scale.set(1, 1);
                this._isShowing = true;
            },
            this._countUp.getCountUpAnimation(endValue, endValue, {duration: 0.01})
        ]);
        if (multiplier > 1) {
            const scale: number = this.getScale(multiplier, multiplier);
            timeline.add(TweenLite.to(this._countUpContainer.scale, 0.2, {
                x: scale,
                y: scale,
                ease: Elastic.easeOut.config(2, 1)
            }), 0);
        }

        return timeline
    }

    private getScale(multiplier: number, finalMultiplier: number): number {
        const maxScale: number = 1.5,
            minScale: number = 1;

        let retVal: number = 1 + ((multiplier - 1) * 0.1);

        if ((1 + ((finalMultiplier - 1) * 0.1)) > maxScale) {
            retVal = 1 + (((maxScale - minScale) / finalMultiplier) * (multiplier - 1));
        }

        return retVal;
    }

    private stopCountUpSound(): void {
        if (this._isCountUpSoundPlaying) {
            SoundManager.stopSoundEffect(WGSoundConfig.instance.COUNTUP_SOUND);
            SoundManager.playSoundEffect(WGSoundConfig.instance.COUNTUP_END);
            this._isCountUpSoundPlaying = false;
        }

        if (!this._hasCountUpEndSoundPlayed) {
            // EventHandler.dispatchEvent(new GameEvent(SoundEvent.PLAY_SOUND_EFFECT, BarbarianFurySoundConfig.instance.COUNT_UP_ENDS));
            this._hasCountUpEndSoundPlayed = true;
        }
    }

    private setMaxTextWidth(multiplier: number): void {
        let val: number;

        if (multiplier === 1) {
            val = 0.9;
        } else {
            val = 0.6;
        }

        this._countUp.maxTextWidth = (120 * 6) * val;
    }

    protected onResize(resizeData: IResizeData): void {
        this._countUpContainer.position.set(120 * 3.2, WGGameConfig.instance.GAME_HEIGHT / 1.8);
        this._countUpContainer.getBounds();
    }
}